import React from 'react';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import userIcon from '../../../common/Images/user.png';
import resourceIcon from '../../../common/Images/server.png';

/**
 * EventLogTemplates - Handles rendering of event log incident headlines
 *
 * This class provides templates for different event log incident types,
 * creating interactive headlines with clickable entity links.
 */
class EventLogTemplates {
    constructor(buildEntityLinkFn, handleInvestigationClickFn) {
        this.buildEntityLink = buildEntityLinkFn;
        this.handleInvestigationClick = handleInvestigationClickFn;
    }

    /**
     * Extract user information from event log data
     * Handles different field name conventions (subject_user_guid vs user_guid, etc.)
     * Returns an object with guid and name for use with buildEntityLink
     */
    extractUserInfo(data) {
        return {
            guid: data.user_guid || data.subject_guid,
            name: data.user_identifier || data.upn || data.sam || data.subject_user_name
        };
    }

    getTemplates() {
        return {
            PrivilegedGroupMemberAddition: (data) => {
                const userInfo = this.extractUserInfo(data);
                const actor = this.buildEntityLink(userInfo.guid, userInfo.name, userIcon, AD_ENTITY_TYPES.USER);
                const member = this.buildEntityLink(
                    data.member_guid,
                    data.member_identifier || data.member_name,
                    userIcon,
                    AD_ENTITY_TYPES.USER
                );
                const group = this.buildEntityLink(
                    data.target_guid,
                    data.target_identifier || data.target_user_name,
                    userIcon,
                    AD_ENTITY_TYPES.GROUP
                );
                const server = this.buildEntityLink(
                    data.server_guid,
                    data.server,
                    resourceIcon,
                    AD_ENTITY_TYPES.SERVER
                );

                return (
                    <h2>
                        {actor || userInfo.name || 'Unknown user'} added user {member || data.member_name || 'unknown'}{' '}
                        to the privileged group {group || data.target_user_name || 'unknown'} on{' '}
                        {server || data.server || 'unknown server'}.
                    </h2>
                );
            },
            SecurityLogCleared: (data) => {
                const userInfo = this.extractUserInfo(data);
                const actor = this.buildEntityLink(userInfo.guid, userInfo.name, userIcon, AD_ENTITY_TYPES.USER);
                const server = this.buildEntityLink(
                    data.server_guid,
                    data.server,
                    resourceIcon,
                    AD_ENTITY_TYPES.SERVER
                );

                return (
                    <h2>
                        The Security Log on {server || data.server || 'unknown server'} was cleared by{' '}
                        {actor || userInfo.name || 'unknown user'}, indicating potential tampering with forensic
                        evidence.
                    </h2>
                );
            },
            RemoteServiceInstallation: (data) => {
                const userInfo = this.extractUserInfo(data);
                const actor = this.buildEntityLink(userInfo.guid, userInfo.name, userIcon, AD_ENTITY_TYPES.USER);
                const server = this.buildEntityLink(
                    data.server_guid,
                    data.server,
                    resourceIcon,
                    AD_ENTITY_TYPES.SERVER
                );
                const serviceName = data.service_name ? ` "${data.service_name}"` : '';

                return (
                    <h2>
                        {actor || userInfo.name || 'Unknown user'} installed a new service{serviceName} on{' '}
                        {server || data.server || 'unknown server'}, potentially indicating remote code execution.
                    </h2>
                );
            },
            NtdsDitDump: (data) => {
                const server = this.buildEntityLink(
                    data.server_guid,
                    data.server,
                    resourceIcon,
                    AD_ENTITY_TYPES.SERVER
                );

                return (
                    <h2>
                        New NTDS.dit database file located in {server || data.server || 'unknown server'}.
                    </h2>
                );
            }
        };
    }

    /**
     * Build a headline for an event log incident
     */
    buildHeadline(data) {
        const incidentKind = data.incident?.kind;

        // Get template from registry
        const templates = this.getTemplates();
        const templateFn = templates[incidentKind];

        if (templateFn) {
            return templateFn(data);
        }

        // Generic fallback
        return <h2>Event log activity detected</h2>;
    }
}

export default EventLogTemplates;
